import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { readStoredUser } from 'lib/authSession';
import { refreshSessionTokens, SESSION_EXPIRED_EVENT } from 'lib/sessionTokens';
import { axios as cachedAxios } from 'setup/axiosSetup';

/**
 * Every call to our API carries the player's access token.
 *
 * Player routes (tickets, statement, deposits, balance) now take the player
 * from the token instead of trusting a player_id in the request, so the token
 * has to be on every request, not only on the handful that set it by hand.
 * An expired token is refreshed once and the request retried; if that fails
 * the session is over and the app logs out.
 */

const API_BASE = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

// Services call `${REACT_APP_GAME_API_BACKEND_API}/...`; without that variable
// they fall back to same-origin relative paths, which are ours too.
const isApiRequest = (url?: string) =>
  !!url && (API_BASE ? url.startsWith(API_BASE) : url.startsWith('/'));

const isTokenEndpoint = (url?: string) =>
  !!url && /\/(login|token\/refresh)\/?(\?|$)/.test(url);

// The browser's own id, sent with a ticket so the risk rules can tell
// accounts played from one device (tottalo_api internal/risk). Random,
// kept in this browser only; it says nothing about the player.
const DEVICE_KEY = 'tottalo.device';
const deviceId = (): string => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id || !/^[A-Za-z0-9-]{8,64}$/.test(id)) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};
const isTicketEndpoint = (url?: string) =>
  !!url && /\/(create_ticket|create_live_ticket)\/?(\?|$)/.test(url);

const attachToken = (config: InternalAxiosRequestConfig) => {
  if (!isApiRequest(config.url) || isTokenEndpoint(config.url)) return config;
  if (isTicketEndpoint(config.url)) {
    const id = deviceId();
    if (id) config.headers.set('X-Device-Id', id);
  }
  if (config.headers?.Authorization) return config;
  const token = readStoredUser()?.access_token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
};

const retryAfterRefresh =
  (instance: AxiosInstance) => async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (
      !config ||
      error.response?.status !== 401 ||
      config._authRetried ||
      !isApiRequest(config.url) ||
      isTokenEndpoint(config.url) ||
      !readStoredUser()
    ) {
      throw error;
    }
    config._authRetried = true;
    const user = await refreshSessionTokens();
    if (!user) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      throw error;
    }
    config.headers.set('Authorization', `Bearer ${user.access_token}`);
    return instance(config);
  };

[axios, cachedAxios as unknown as AxiosInstance].forEach((instance) => {
  instance.interceptors.request.use(attachToken);
  instance.interceptors.response.use(undefined, retryAfterRefresh(instance));
});
